import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useResizeObserver } from 'usehooks-ts';
import { Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import GraphTimeline from 'components/GraphTimeline';
import { TimeTickColor } from 'components/GraphTimeline/GraphTimeline';
import type { CrowdTimestep } from 'types/crowd';

import useCrowdGraph from './hooks/useCrowdGraph';

import graphStyles from '../../../Graphs.module.scss';
import styles from './CrowdGraph.module.scss';

interface CrowdGraphProps {
  data: {
    observed: Array<CrowdTimestep | undefined> | null;
    predicted: Array<CrowdTimestep | undefined> | undefined;
  };
}

const CrowdGraph: React.FC<CrowdGraphProps> = ({ data }) => {
  const canvasRef = React.useRef<HTMLDivElement | null>(null);

  const { height: graphHeight = 91, width: graphWidth = 320 } = useResizeObserver({
    ref: canvasRef,
  });

  const { setupGraph } = useCrowdGraph({ canvasRef, data, graphHeight, graphWidth });

  const debouncedSetupGraph = useMemo(
    () => debounce(setupGraph, 100, { leading: true, trailing: true }),
    [setupGraph],
  );

  useEffect(() => {
    if (graphWidth) debouncedSetupGraph();

    return () => {
      debouncedSetupGraph.cancel();
    };
  }, [graphWidth, debouncedSetupGraph]);

  return (
    <div className={styles.crowdGraph} data-testid="camera-insights-crowd-graph">
      <dl
        className={classNames(graphStyles.legend, styles.legend)}
        data-testid="camera-insights-crowd-legend"
      >
        <Typography
          component="dt"
          variant="caption2"
          color="text.secondary"
          className={styles.legendPredicted}
        >
          Predicted
        </Typography>
        <dd style={visuallyHidden}>Today&apos;s predicted crowd</dd>
        <Typography
          component="dt"
          variant="caption2"
          color="text.secondary"
          className={styles.legendObserved}
        >
          Observed
        </Typography>
        <dd style={visuallyHidden}>Today&apos;s observed crowd</dd>
      </dl>
      <div className={styles.graph} ref={canvasRef} />
      <GraphTimeline day={data.predicted as any} showMarkers timeTickColor={TimeTickColor.DARK} />
    </div>
  );
};

export default CrowdGraph;
